import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactNode } from "react";
import { CatalogEntityType, CatalogMetricType, ItemUsage } from "@models/catalog.model";
import { UsageChip } from "./usage";

// Dimensions have their own authoring box in the page (it also shows observed values),
// so this panel covers only the two remaining structure sections.
export type StructureKind = "entityType" | "dimension" | "metricType";

const HEAD = {
  fontWeight: 600, color: "#8c9097", fontSize: 12,
  textTransform: "uppercase" as const, letterSpacing: 0.3,
};

function Section<T extends { id: number }>({
  title, addLabel, items, primary, secondary, trailing, onAdd, onEdit, onDelete,
}: {
  title: string;
  addLabel: string;
  items: T[];
  primary: (item: T) => string;
  secondary: (item: T) => string;
  trailing?: (item: T) => ReactNode;
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 240, p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography sx={HEAD}>{title}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd} sx={{ ml: "auto", textTransform: "none" }}>
          {addLabel}
        </Button>
      </Box>
      {items.length === 0 && <Box sx={{ color: "#c4c4c4", fontSize: 13 }}>None yet</Box>}
      {items.map((item) => (
        <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5,
          "&:hover .row-actions": { opacity: 1 } }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>
              {primary(item)}
            </Box>
            {secondary(item) && <Box sx={{ color: "#8c9097", fontSize: 11 }}>{secondary(item)}</Box>}
          </Box>
          {trailing?.(item)}
          <Box className="row-actions" sx={{ ml: "auto", opacity: 0, transition: "opacity .15s", whiteSpace: "nowrap" }}>
            <Tooltip title="Edit">
              <IconButton size="small" aria-label="Edit" onClick={() => onEdit(item)}><EditOutlinedIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" aria-label="Delete" onClick={() => onDelete(item)}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function StructurePanel({
  entityTypes, metricTypes, verticalId, entityTypeUsage, onAdd, onEditEntityType, onEditMetricType, onDelete,
}: {
  entityTypes: CatalogEntityType[];
  entityTypeUsage?: Record<number, ItemUsage>;
  metricTypes: CatalogMetricType[];
  verticalId: number;
  onAdd: (kind: StructureKind) => void;
  onEditEntityType: (t: CatalogEntityType) => void;
  onEditMetricType: (t: CatalogMetricType) => void;
  onDelete: (kind: StructureKind, id: number, label: string) => void;
}) {
  const typeName = (id: number | null) =>
    entityTypes.find((t) => t.id === id)?.code ?? "";
  // Base/global types + this vertical's domain types are the ones relevant here.
  const relevantMetricTypes = metricTypes.filter((t) => t.vertical_id == null || t.vertical_id === verticalId);

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
      <Section
        title="Entity types" addLabel="Entity type" items={entityTypes}
        primary={(t) => `${t.name} (${t.code})`}
        secondary={(t) => [t.parent_type_id ? `↳ parent: ${typeName(t.parent_type_id)}` : "root",
          t.is_reference ? "reference" : ""].filter(Boolean).join(" · ")}
        trailing={(t) => <UsageChip usage={entityTypeUsage?.[t.id]} />}
        onAdd={() => onAdd("entityType")}
        onEdit={onEditEntityType}
        onDelete={(t) => onDelete("entityType", t.id, t.code)}
      />
      <Section
        title="Metric types" addLabel="Metric type" items={relevantMetricTypes}
        primary={(t) => `${t.name} (${t.code})`}
        secondary={(t) => [t.base_kind, t.vertical_id == null ? "base" : "",
          t.allowed_values.length ? `${t.allowed_values.length} values` : ""].filter(Boolean).join(" · ")}
        onAdd={() => onAdd("metricType")}
        onEdit={onEditMetricType}
        onDelete={(t) => onDelete("metricType", t.id, t.code)}
      />
    </Box>
  );
}
